import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useDebouncedCallback } from '@tanstack/react-pacer';
import moment from 'moment';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import ControlledDateRangePicker from '../../components/BasicUI/ControlledInputs/ControlledDateRangePicker';
import ControlledGuestCountSelect
  from '../../components/BasicUI/ControlledInputs/ControlledGuestCountSelect/ControlledGuestCountSelect';
import config from '../../config';

import { isFlexPass, selectedDateOverlapsOpenBooking } from '../../util/annualPass';
import { isCampingGroundListing } from '../../util/data';
import useAppSelector from '../../util/hooks/useAppSelector';
import usePass from '../../util/hooks/usePass';
import { findOptionsForSelectFilter } from '../../util/search';
import { REQUEST_BOOKING } from '../../util/transaction.default';
import { shouldDisableDateFn } from './BookingDatesForm.new.helpers';
import BookingModal from './BookingModal';
import CustomLineItemsStep from './CustomLineItemsStep';
import ExtraLineItemsStep from './ExtraLineItemsStep';
import VoucherStep from './VoucherStep';

const getCategoryLabel = (categories, key, intl) => {
  const cat = categories.find((c) => c.key === key);
  return cat ? intl.formatMessage({ id: cat.label }) : key;
};

const diffTodayAndDate = (date) => {
  const todayMonth = moment().month(); // September would be 8
  const todayYear = moment().year();
  const targetMonth = moment(date).month(); // January would be 0
  const targetYear = moment(date).year();

  // Calculate the difference in years and months
  let yearDifference = targetYear - todayYear;
  let monthDifference = targetMonth - todayMonth;

  // Adjust for negative month difference
  if (monthDifference < 0) {
    yearDifference -= 1;
    monthDifference += 12;
  }

  // Return total difference in months
  return yearDifference * 12 + monthDifference;
};

const formatStepLabel = (label, selectedCount, intl) => {
  if (selectedCount > 0) {
    const countText = intl.formatMessage(
      { id: 'BookingDatesForm.selectedCount' },
      { count: selectedCount },
    );
    return `${label} (${countText})`;
  }
  return label;
};

const createSteps = ({
  appliedVoucher,
  control,
  currentListing,
  disabled,
  extraLineItemsSelectedCount,
  customLineItemsSelectedCount,
  intl,
  onApplyVoucher,
  voucherValidationError,
  voucherValidationInProgress,
}) => {
  const extraLineItems = currentListing?.attributes?.publicData?.extraLineItems || {};
  const customLineItems = currentListing?.attributes?.publicData?.customLineItems || {};

  const extraLineItemsLabel = intl.formatMessage({ id: 'BookingDatesForm.extraLineItemsStepLabel' });
  const customLineItemsLabel = intl.formatMessage({ id: 'BookingDatesForm.customLineItemsStepLabel' });
  const voucherStepLabel = intl.formatMessage({ id: 'BookingDatesForm.voucherStepLabel' });

  return [
    ...(Object.keys(extraLineItems).length > 0 ? [{
      label: formatStepLabel(extraLineItemsLabel, extraLineItemsSelectedCount, intl),
      component: (
        <ExtraLineItemsStep
          control={control}
          disabled={disabled}
          extraLineItems={extraLineItems}
        />
      ),
    }] : []),
    ...(Object.keys(customLineItems).length > 0 ? [{
      label: formatStepLabel(customLineItemsLabel, customLineItemsSelectedCount, intl),
      component: (
        <CustomLineItemsStep
          control={control}
          customLineItems={customLineItems}
          disabled={disabled}
        />
      ),
    }] : []),
    {
      label: voucherStepLabel,
      component: (
        <VoucherStep
          appliedVoucher={appliedVoucher}
          onApplyVoucher={onApplyVoucher}
          voucherValidationError={voucherValidationError}
          voucherValidationInProgress={voucherValidationInProgress}
        />
      ),
    },
  ];
};

const createGuestCount = (guestOptions = null, isCampingGround, intl) => {
  const maxPets = guestOptions?.maxPetCount || 0;

  return [
    {
      name: 'adult',
      label: intl.formatMessage({ id: 'BookingDatesForm.adultsLabel' }),
      infoLabel: intl.formatMessage({ id: 'BookingDatesForm.adultsInfoLabel' }),
      minimum: 1,
    },
    {
      name: 'child',
      label: intl.formatMessage({ id: 'BookingDatesForm.childrenLabel' }),
      infoLabel: intl.formatMessage({ id: 'BookingDatesForm.childrenInfoLabel' }),
      minimum: 0,
    },
    {
      name: 'toddler',
      label: intl.formatMessage({ id: 'BookingDatesForm.toddlersLabel' }),
      infoLabel: intl.formatMessage({ id: 'BookingDatesForm.toddlersInfoLabel' }),
      minimum: 0,
    },
    ...(isCampingGround ? [{
      name: 'pet',
      label: intl.formatMessage({ id: 'BookingDatesForm.petsLabel' }),
      minimum: 0,
      maximum: maxPets,
      excludeFromTotal: true,
    }] : []),
  ];
};

const createCurrentGuestCountLabels = (intl) => ({
  adult: (count) => intl.formatMessage({ id: 'BookingDatesForm.adult.countLabel' }, { count }),
  child: (count) => intl.formatMessage({ id: 'BookingDatesForm.child.countLabel' }, { count }),
  toddler: (count) => intl.formatMessage({ id: 'BookingDatesForm.toddler.countLabel' }, { count }),
  pet: (count) => intl.formatMessage({ id: 'BookingDatesForm.pet.countLabel' }, { count }),
});

/**
 * @param {import('@vansite/ts-sharetribe-flex-sdk').Listing & {images: import('@vansite/ts-sharetribe-flex-sdk').Image[]}} currentListing
 * @param {number} distanceToLocation
 * @param {boolean} fetchTimeSlotsInProgress
 * @param {(number) => void} onFetchTimeSlots
 * @param {import('@vansite/ts-sharetribe-flex-sdk').TimeSlot[]} timeSlots
 * @returns {JSX.Element}
 * @constructor
 */

const BookingDatesFormNewComponent = ({
  appliedVoucher = null,
  autoOpenModal = false,
  currentListing,
  distanceToLocation,
  fetchLineItemsInProgress = false,
  fetchTimeSlotsInProgress,
  initialDates = null,
  lineItems = [],
  onApplyVoucher,
  onBookingDataChange,
  onSubmit,
  onFetchTimeSlots,
  showAnnualPassError,
  timeSlots,
  voucherValidationError = null,
  voucherValidationInProgress = false,
  bookingLimit,
  minimumNights = 1,
}) => {
  const intl = useIntl();

  // initialDates is a validated [moment, moment] tuple provided by the caller
  // (see BookingPanel URL parsing). Falls back to [null, null] so the form
  // behaves the same for organic visits without query params.
  const initialBookingDates = useMemo(
    () => (initialDates && initialDates[0] && initialDates[1] ? initialDates : [null, null]),
    [initialDates],
  );

  const [openModal, setOpenModal] = useState(() => autoOpenModal && Boolean(initialBookingDates[0] && initialBookingDates[1]));

  const isCampingGround = isCampingGroundListing(currentListing);
  const guestOptions = useMemo(() => createGuestCount(currentListing.attributes.publicData.guestOptions, isCampingGround, intl), [currentListing.attributes.publicData.guestOptions, isCampingGround, intl]);

  const {
    formState: { errors },
    control,
    watch,
    setValue,
    handleSubmit,
  } = useForm({
    defaultValues: {
      bookingDates: initialBookingDates,
      guestCount: guestOptions.reduce((acc, guestOption) => ({
        ...acc,
        [guestOption.name]: guestOption.name === 'adult' && guestOption.minimum === 1 ? 2 : guestOption.minimum,
      }), {}),
      voucherCode: '',
      extraLineItems: {},
      customLineItems: {},
    },
  });

  const countLabels = useMemo(() => createCurrentGuestCountLabels(intl), [intl]);
  const completeBookingLabel = intl.formatMessage({ id: 'BookingDatesForm.completeBookingLabel' });

  const categoryKey = currentListing.attributes.publicData.category ? currentListing.attributes.publicData.category : REQUEST_BOOKING;
  const categoryOptions = findOptionsForSelectFilter('category', config.custom.filters);
  const categoryLabel = <span>{getCategoryLabel(categoryOptions, categoryKey, intl)}</span>;

  const bookingDates = watch('bookingDates');
  const guestCount = watch('guestCount');

  // Hard-block the same-date annual-pass overlap that the backend otherwise
  // strips silently (see selectedDateOverlapsOpenBooking / the backend
  // `thisTransactionIsTheSameDateAsAnOpenTransaction` guard). The calendar
  // already greys out overlapping slots, but that relies on the open-bookings
  // list having loaded before the time slots — a race that let users book the
  // same period twice and lose the discount with no explanation. Mirrors the
  // backend scope: production-only, and only the regular annual pass (25-pass /
  // FlexPass are exempt from the open-booking limits).
  const currentUserOpenBookings = useAppSelector((state) => state.user.currentUserOpenBookings);
  const [passIsValid, pass] = usePass();
  const passWithOpenBookingLimit = !(pass?.twentyFivePass || isFlexPass(pass));
  const annualPassDateOverlapError =
    process.env.REACT_APP_ENV === 'production' &&
    passIsValid &&
    passWithOpenBookingLimit &&
    selectedDateOverlapsOpenBooking(bookingDates[0], currentUserOpenBookings)
      ? intl.formatMessage({ id: 'BookingDatesForm.annualPassDateOverlap' })
      : null;
  // showAnnualPassError covers the count-based limits (already shown by the
  // parent panel); merge the date-overlap so both gate the submit equally.
  const effectiveAnnualPassError = showAnnualPassError || annualPassDateOverlapError;

  // Use useWatch for line items to get proper reactivity (triggers re-renders on nested changes)
  const formExtraLineItems = useWatch({ control, name: 'extraLineItems' }) || {};
  const formCustomLineItems = useWatch({ control, name: 'customLineItems' }) || {};

  // Calculate selected counts for step labels
  const extraLineItemsSelectedCount = useMemo(
    () => Object.values(formExtraLineItems).filter((item) => item?.add).length,
    [formExtraLineItems],
  );
  const customLineItemsSelectedCount = useMemo(
    () => Object.values(formCustomLineItems).filter((item) => item?.add).length,
    [formCustomLineItems],
  );

  const steps = useMemo(() => createSteps({
    appliedVoucher,
    control,
    currentListing,
    customLineItemsSelectedCount,
    disabled: fetchLineItemsInProgress,
    extraLineItemsSelectedCount,
    intl,
    onApplyVoucher,
    voucherValidationError,
    voucherValidationInProgress,
  }), [appliedVoucher, control, currentListing, customLineItemsSelectedCount, extraLineItemsSelectedCount, fetchLineItemsInProgress, intl, onApplyVoucher, voucherValidationError, voucherValidationInProgress]);

  // Get listing's line item definitions for merging with form selections
  // Memoize to avoid new {} references each render (would re-trigger useEffect)
  const listingExtraLineItems = useMemo(
    () => currentListing?.attributes?.publicData?.extraLineItems || {},
    [currentListing?.attributes?.publicData?.extraLineItems],
  );
  const listingCustomLineItems = useMemo(
    () => currentListing?.attributes?.publicData?.customLineItems || {},
    [currentListing?.attributes?.publicData?.customLineItems],
  );

  const getGuestDisplayValue = useCallback((value = {}) => {
    const parts = Object.keys(value)
      .map((key) => {
        const count = value[key];
        if (count === 0) return null;
        return countLabels[key] ? countLabels[key](count) : null;
      })
      .filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '';
  }, [countLabels]);

  // Track previous start date to detect changes
  const prevStartDateRef = useRef(bookingDates[0]);

  // Debounced callback for guest count changes (500ms delay)
  const debouncedOnBookingDataChange = useDebouncedCallback(
    (data) => {
      if (onBookingDataChange) {
        onBookingDataChange(data);
      }
    },
    { wait: 500 },
  );

  // Track previous values to detect what changed. Start bookingDates at
  // [null, null] so when the form mounts with pre-filled initialDates the
  // first effect run detects a change and triggers the line-items fetch.
  const prevBookingDatesRef = useRef([null, null]);
  const prevGuestCountRef = useRef(guestCount);
  const prevExtraLineItemsRef = useRef(formExtraLineItems);
  const prevCustomLineItemsRef = useRef(formCustomLineItems);

  // Process form line items to extract only selected items with their full data
  const processLineItems = useCallback((formItems, listingItems, maxQuantity = 10) => {
    const result = {};
    if (!formItems) return result;

    Object.keys(formItems).forEach((key) => {
      if (formItems[key]?.add && listingItems[key]) {
        const quantity = Math.min(
          Math.max(1, parseInt(formItems[key].quantity, 10) || 1),
          maxQuantity,
        );
        result[key] = {
          price: listingItems[key].price,
          quantity,
        };
      }
    });
    return result;
  }, []);

  const handleFormSubmit = (values) => {
    const [bookingStart, bookingEnd] = values.bookingDates || [];
    // Guard against an incomplete range — the form can submit before both
    // dates are picked, and toISOString() on a null date throws.
    if (!bookingStart || !bookingEnd) {
      return;
    }
    const extraLineItems = currentListing?.attributes?.publicData?.extraLineItems || {};
    const customLineItems = currentListing?.attributes?.publicData?.customLineItems || {};

    onSubmit({
      bookingDates: {
        bookingStart: bookingStart.toISOString(),
        bookingEnd: bookingEnd.toISOString(),
      },
      bookingData: {
        guestCount: {
          adultCount: values.guestCount.adult,
          childCount: values.guestCount.child,
          toddlerCount: values.guestCount.toddler,
          petCount: values.guestCount.pet,
        },
        voucher: { code: values.voucherCode },
        extraLineItems: Object.entries(values.extraLineItems).reduce((acc, [key, value]) => {
          if (value.add) {
            acc[key] = {
              ...extraLineItems[key],
            };
          }
          return acc;
        }, {}),
        customLineItems: Object.entries(values.customLineItems).reduce((acc, [key, value]) => {
          if (value.add) {
            acc[key] = {
              ...customLineItems[key],
              quantity: value.quantity,
            };
          }
          return acc;
        }, {}),
      },
    });
  };

  // Notify parent when booking data changes
  useEffect(() => {
    if (!onBookingDataChange) return;

    const bookingDatesChanged = JSON.stringify(prevBookingDatesRef.current) !== JSON.stringify(bookingDates);
    const guestCountChanged = JSON.stringify(prevGuestCountRef.current) !== JSON.stringify(guestCount);
    const extraLineItemsChanged = JSON.stringify(prevExtraLineItemsRef.current) !== JSON.stringify(formExtraLineItems);
    const customLineItemsChanged = JSON.stringify(prevCustomLineItemsRef.current) !== JSON.stringify(formCustomLineItems);

    prevBookingDatesRef.current = bookingDates;
    prevGuestCountRef.current = guestCount;
    prevExtraLineItemsRef.current = formExtraLineItems;
    prevCustomLineItemsRef.current = formCustomLineItems;

    // Skip if nothing actually changed (avoids infinite loops from unstable references)
    if (!bookingDatesChanged && !guestCountChanged && !extraLineItemsChanged && !customLineItemsChanged) return;

    // Process line items to send only selected items with full data
    const extraLineItemsToSend = processLineItems(formExtraLineItems, listingExtraLineItems);
    const customLineItemsToSend = processLineItems(formCustomLineItems, listingCustomLineItems);

    const bookingData = {
      bookingDates,
      customLineItems: customLineItemsToSend,
      extraLineItems: extraLineItemsToSend,
      guestCount,
    };

    if (guestCountChanged || extraLineItemsChanged || customLineItemsChanged) {
      // Debounce changes that might happen rapidly
      debouncedOnBookingDataChange(bookingData);
    } else {
      // Immediate update for date changes
      onBookingDataChange(bookingData);
    }
  }, [
    bookingDates,
    debouncedOnBookingDataChange,
    formCustomLineItems,
    formExtraLineItems,
    guestCount,
    listingCustomLineItems,
    listingExtraLineItems,
    onBookingDataChange,
    processLineItems,
  ]);

  // Always reset end date when start date changes to ensure user re-selects
  useEffect(() => {
    const prevStartDate = prevStartDateRef.current;
    const currentStartDate = bookingDates[0];
    const currentEndDate = bookingDates[1];

    // Check if start date actually changed (not just on initial mount)
    const startDateChanged = prevStartDate && currentStartDate &&
      !moment(prevStartDate).isSame(currentStartDate, 'day');

    if (startDateChanged && currentEndDate) {
      // Always reset end date when start date changes to ensure user re-selects
      setValue('bookingDates', [currentStartDate, null]);
    }

    // Update ref for next comparison
    prevStartDateRef.current = currentStartDate;
  }, [bookingDates[0], bookingDates[1], setValue]);

  // Combined shouldDisableDate – disables unavailable/booked dates and dates outside valid range
  // MUI DateRangePicker passes (day, position) where position is "start" or "end"
  const shouldDisableDate = useMemo(
    () => shouldDisableDateFn(timeSlots, bookingDates[0], bookingLimit, minimumNights),
    [timeSlots, bookingDates[0], bookingLimit, minimumNights],
  );

  return (
    <Stack component="form" direction="column" spacing={2} sx={{ width: '100%' }}>
      <ControlledDateRangePicker
        control={control}
        datePickerProps={{
          loading: fetchTimeSlotsInProgress,
          shouldDisableDate,
          onMonthChange: (date) => onFetchTimeSlots(diffTodayAndDate(date)),
        }}
        error={!!errors.bookingDates}
        helperText={errors.bookingDates?.message}
        name="bookingDates"
      />
      <ControlledGuestCountSelect
        control={control}
        countLabels={countLabels}
        guestOptions={guestOptions}
        label={intl.formatMessage({ id: 'BookingDatesForm.guestCountLabel' })}
        name="guestCount"
        totalMaximum={currentListing.attributes.publicData.guestOptions?.maxGuestCount || 2}
      />
      <Typography color="text.secondary" variant="caption">
        {intl.formatMessage(
          { id: 'BookingDatesForm.maxGuestCountInfo' },
          { count: currentListing.attributes.publicData.guestOptions?.maxGuestCount || 2 },
        )}
      </Typography>
      {annualPassDateOverlapError && !showAnnualPassError ? (
        <Alert severity="warning">{annualPassDateOverlapError}</Alert>
      ) : null}
      <Button
        disabled={
          !bookingDates[0] ||
          !bookingDates[1] ||
          fetchLineItemsInProgress ||
          !!effectiveAnnualPassError
        }
        fullWidth
        loading={fetchLineItemsInProgress}
        onClick={() => setOpenModal(true)}
        size="large"
        variant="contained"
      >
        {completeBookingLabel}
      </Button>
      <BookingModal
        bookingDates={bookingDates}
        bookingLimit={bookingLimit}
        categoryLabel={categoryLabel}
        currentListing={currentListing}
        distanceToLocation={distanceToLocation}
        fetchLineItemsInProgress={fetchLineItemsInProgress}
        fetchTimeSlotsInProgress={fetchTimeSlotsInProgress}
        getGuestDisplayValue={getGuestDisplayValue}
        guestCount={guestCount}
        guestOptions={guestOptions}
        lineItems={lineItems}
        minimumNights={minimumNights}
        onBookingDatesChange={(newValue) => setValue('bookingDates', newValue)}
        onClose={() => setOpenModal(false)}
        onGuestCountChange={(newValue) => setValue('guestCount', newValue)}
        onMonthChange={(date) => onFetchTimeSlots(diffTodayAndDate(date))}
        onSubmit={handleSubmit(handleFormSubmit)}
        open={openModal}
        showAnnualPassError={effectiveAnnualPassError}
        steps={steps}
        timeSlots={timeSlots}
      />
    </Stack>
  );
};

const BookingDatesForm = memo(BookingDatesFormNewComponent);

export default BookingDatesForm;